import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import ScheduleTable from "../../components/forms/scheduleForm/ScheduleTable";
import { 
  initialScheduleState,
  ensureScheduleStructure,
  isNormalShift,
  validateScheduleTimes,
  shouldDisableTimeInputs,
  generateScheduleCode,
  generateLegend,
  formatScheduleType,
  initializePredefinedSchedules,
  calculateWeightTime
} from "../../components/utils/scheduleUtils";
import { useLocation, useNavigate } from "react-router-dom";
import NavigationButton from "../../components/ui/NavigationButton";
import { TimeInputGroup } from "../../components/forms/scheduleForm/TimeInputGroup";
import { MealBreakSection } from "../../components/forms/scheduleForm/MealBreakSection";
import { InputField } from "../../components/forms/scheduleForm/InputField";
import { ShiftTypesSection } from "../../components/forms/scheduleForm/ShiftTypeSection";
import BackButton from "../../components/ui/BackNavigationButton";
import Tooltip from "../../components/ui/ToolTip";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { pages } from "../../components/utils/pages-links";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import { useAlert } from "../../context/alert/AlertProvider";
import { useTranslation } from 'react-i18next';
import { usePageTooltips } from "../../hooks/usePageTooltips";
import ScenarioIndicator from "../../components/forms/scenario/ScenarioIndicator";
import useAutoSave from "../../hooks/useAutoSave";

function SchedulePage() {
  const [schedule, setSchedule] = useState(initialScheduleState);
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const navigate = useNavigate();
  const {showAlert} = useAlert();
  const tooltips = usePageTooltips();
 
  const scheduleFormRef = useRef(null);
  const scheduleTableRef = useRef(null);
  const nextButtonRef = useRef(null);
  const location = useLocation();

  const isExampleMode = location.state?.isExample;

  const { t } = useTranslation();

  useAutoSave('schedules', savedSchedules);

  const tutorialSteps = [
    {
      title: t("tutorial.schedule.title"),
      description: t("tutorial.schedule.description"),
      position:'center',
    },
    {
      title: t("tutorial.schedule.details.title"),
      description: t("tutorial.schedule.details.description"),
      position:"top-right",
      elementRef: scheduleFormRef,
    },
    {
      title: t("tutorial.schedule.manage.title"),
      description: t("tutorial.schedule.manage.description"),
      position: "top-right",
      elementRef: scheduleTableRef,
    },
    {
      title: t("tutorial.schedule.next.title"),
      description: t("tutorial.schedule.next.description"),
      position: "next-button",
      elementRef:nextButtonRef,
    },
  ];

  useEffect(() => {
    if(isExampleMode){
      setShowTutorial(true);
    }

    const storedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
    
    if (storedSchedules.length > 0) {
      setSavedSchedules(storedSchedules);
    } else {
      const initializedSchedules = initializePredefinedSchedules();
      setSavedSchedules(initializedSchedules);
    }
  }, [isExampleMode]);

  const updateScheduleWithGeneratedFields = (updates) => {
    const newSchedule = { ...schedule, ...updates };
    const activeShift = newSchedule.shiftTypes.find(shift => shift.active && shift.type);
    
    setSchedule({
      ...newSchedule,
      code: generateScheduleCode(newSchedule.shiftTypes, newSchedule.weightTime),
      legend: generateLegend(newSchedule),
      weightTime: activeShift?.type === 'Off' ? '0' : calculateWeightTime(newSchedule)
    });
  };

  const formatHoursToTimeString = (value) => {
    if (value === null || value === undefined) return '';
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  const handleTimeChange = (field, value) => {
    updateScheduleWithGeneratedFields({ [field]: value });
  };

  const handleDescriptionChange = (value) => {
    setSchedule(prev => ({ ...prev, description: value }));
  };

  const handleMealBreakToggle = (checked) => {
    const newMealBreaks = [{
      enabled: checked,
      startTime: '',
      endTime: ''
    }];
    updateScheduleWithGeneratedFields({ mealBreaks: newMealBreaks });
  };

  const handleMealBreakTimeChange = (field, value) => {
    const newMealBreaks = [{
      ...schedule.mealBreaks[0],
      [field]: value
    }];
    updateScheduleWithGeneratedFields({ mealBreaks: newMealBreaks });
  };

  const handleShiftChange = (index, shiftUpdate) => {
    const newShiftTypes = [...schedule.shiftTypes];
    newShiftTypes[index] = {
      ...newShiftTypes[index],
      ...shiftUpdate,
      active:true
    };
    updateScheduleWithGeneratedFields({ shiftTypes: newShiftTypes });
  };

  const handleShiftDeactivate = (index) => {
    const newShiftTypes = [...schedule.shiftTypes];
    newShiftTypes[index] = { active: false, type: "" };
    updateScheduleWithGeneratedFields({ shiftTypes: newShiftTypes });
  };

  const handleSave = () => {
    const activeShift = schedule.shiftTypes.find(shift => shift.active && shift.type);
    
    if (!activeShift) {
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.oneShift"),
        variant:'destructive'
      })
      return;
    }

    if (isNormalShift(activeShift.type)) {
      const validation = validateScheduleTimes(schedule,t);

      if (!schedule.weightTime.trim() || !validation.isValid) {
        showAlert(validation.error);
        return;
      }
    }

    const formattedSchedule = {
      ...schedule,
      schedule_type: formatScheduleType(schedule.shiftTypes.find(s => s.active)?.type || ''),
      mealBreaks: schedule.mealBreaks.filter(break_ => break_.enabled),
      shiftTypes: schedule.shiftTypes
        .filter(shift => shift.active && shift.type)
        .map(shift => ({
          active: shift.active,
          type: shift.type
        })),
      startTime: schedule.startTime || 0,
      endTime: schedule.endTime || 0,
    };

    if (isEditing) {
      setSavedSchedules(prev => {
        const newSchedules = [...prev];
        newSchedules[editingIndex] = formattedSchedule;
        return newSchedules;
      });
      setIsEditing(false);
      setEditingIndex(null);
    } else {
      setSavedSchedules(prev => [...prev, formattedSchedule]);
    }

    setSchedule(initialScheduleState);
  };

  const handleEdit = (index, scheduleToEdit) => {
    setEditingIndex(index);
    setSchedule(ensureScheduleStructure(scheduleToEdit));
    setIsEditing(true);
  };

  const handleNext = () => {
    if (savedSchedules.length === 8) {
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.oneSchedule"),
        variant:'destructive'
      })
      return;
    }
    
    console.log(savedSchedules)

    navigate('/form/legislation', {
      state: { isExample: isExampleMode }
    });
    
  };

  const handleBack = () => {
    navigate('/form/general' , {
      state: { isExample: isExampleMode }
  });
  }

  const handleNextTutorialStep = () => {
    if(tutorialStep < tutorialSteps.length - 1){
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);    }
  }

  return (
    <div className="-mt-16">
      <Breadcrumbs pages={pages}/>
      <div className="p-6 bg-gray-100 min-h-screen mx-auto">
        <div className="max-w-5xl mx-auto">

          <div className="flex justify-between items-center mb-4">
            <BackButton onClick={handleBack} />
            <NavigationButton ref={nextButtonRef} onClick={handleNext} className="mb-4" />
          </div>

          <ScenarioIndicator />

          <Card ref={scheduleFormRef} className="max-w-5xl mx-auto mt-5">
            <CardContent className="p-6">
              <div className="lg:flex lg:gap-6 md:block sm:block">
                <div className="lg:w-1/2 md:w-full sm:w-full mb-6 lg:mb-0">
                  <div className="flex justify-center">
                    <h2 className="text-2xl font-bold mb-6">{t("form.schedule.title")}</h2>
                    <Tooltip content={tooltips.schedule}/>
                  </div>
                  <div className="w-full lg:w-[80%] mx-auto lg:mx-0 space-y-4">
                    <div className="space-y-4 mb-6">
                      <TimeInputGroup
                        label={t("form.schedule.start")}
                        day={schedule.startDay}
                        time={schedule.startTime}
                        onDayChange={(v) => updateScheduleWithGeneratedFields({ startDay: v })}
                        onTimeChange={(v) => handleTimeChange("startTime", v)}
                        disabled={shouldDisableTimeInputs(schedule) || isExampleMode}
                        translations={t}
                      />
                      
                      <TimeInputGroup
                        label={t("form.schedule.end")}
                        day={schedule.endDay}
                        time={schedule.endTime}
                        onDayChange={(v) => updateScheduleWithGeneratedFields({ endDay: v })}
                        onTimeChange={(v) => handleTimeChange("endTime", v)}
                        disabled={shouldDisableTimeInputs(schedule) || isExampleMode}
                        translations={t}
                      />
                    </div>

                    <MealBreakSection
                      mealBreak={schedule.mealBreaks[0]}
                      onToggle={handleMealBreakToggle}
                      onTimeChange={handleMealBreakTimeChange}
                      disabled={shouldDisableTimeInputs(schedule) || isExampleMode}
                      translations={t}
                    />

                    <ShiftTypesSection
                      shiftTypes={schedule.shiftTypes}
                      onChange={handleShiftChange}
                      onDeactivate={handleShiftDeactivate}
                      translations={t}
                    />
                  </div>
                </div>

                <div className="lg:w-1/2 md:w-full sm:w-full">
                  <div className="w-full lg:w-[80%] mx-auto lg:mx-0 space-y-4 mt-8 lg:mt-0">
                    <InputField
                      label={t("form.schedule.legend")}
                      value={schedule.legend}
                      disabled={true}
                      className="bg-gray-200"
                      readOnly={true}
                    />

                    <InputField
                      label={t("form.schedule.description")}
                      value={schedule.description}
                      onChange={handleDescriptionChange}
                      disabled={isExampleMode}
                    />

                    <InputField
                      label={t("form.schedule.weightTime")}
                      value={formatHoursToTimeString(schedule.weightTime)}
                      onChange={(value) => handleTimeChange("weightTime", value)}
                      disabled={isExampleMode}
                      readOnly={true}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-14">
                <button
                  onClick={handleSave}
                  className={`px-5 py-2 rounded-md transition-colors
                    ${
                      isExampleMode 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                >
                  {isEditing ? t("form.schedule.update") : t("form.schedule.save")}
                </button>
                <button
                  className={`px-5 py-2 rounded-md transition-colors
                    ${
                      isExampleMode 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-red-100 text-red-600 hover:bg-red-200'
                    }`}
                  onClick={()=>{
                    setSchedule(initialScheduleState);
                    setIsEditing(false);
                    setEditingIndex(null);
                  }}
                >
                  {t("form.schedule.cancel")}
                </button>
              </div>
            </CardContent>
          </Card>

          <ScheduleTable
            schedules={savedSchedules}
            onEdit={handleEdit}
            onDelete={(index) => setSavedSchedules(prev => prev.filter((_, i) => i !== index))}
            ref={scheduleTableRef}
            isDisabled={isExampleMode}
          />
        </div>
      </div>

      <TutorialOverlay 
        isVisible={showTutorial}
        onClose={()=> setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
}

export default SchedulePage;